<!-- COVER SHEET — pick what goes on the page, then optionally fill it in.
     THE COVER SHEET IS OPTIONAL AND SKIPPING IT IS THE DEFAULT PATH. A photo
     with no cover sheet is still a fax, and a parent with a toddler on her hip
     needs this done in thirty seconds, one-handed. TO is the only field most
     people will ever touch, and it is remembered.

     Real <label> elements, never placeholder-only: a placeholder disappears the
     moment you type, which is exactly when someone with brain fog needs it. -->
<template>
  <IonPage>
    <AppHeader title="Cover Sheet" back="/tabs/machine" />
    <IonContent :fullscreen="true">

      <div class="pad stack" style="margin-top: var(--sp-3)">
        <!-- The one plain sentence, before the first send only. Sentence case,
             deliberately outside the machine's voice, because the machine's
             voice is what misled six people in testing: one nearly used this
             for real work faxes, one believed a message had reached a named
             person and would have blamed himself when no reply came. -->
        <div v-if="!m.toldOnce" class="plain-note">
          <p style="margin: 0 0 var(--sp-2)">
            This app doesn't deliver faxes. It makes a picture, and you send that
            picture yourself in the next step.
          </p>
          <IonButton size="small" fill="clear" @click="understood">Got it</IonButton>
        </div>

        <!-- THE TRAYS ARE THE PLACEHOLDER. Three buttons for a picture that
             is already there was a third of the screen spent on what had been
             done; once something is loaded, the picture takes their place with
             a CLEAR key on its corner, and clearing brings the trays back. -->
        <div class="stack">
          <div v-if="!hasImage" class="stack media-slot">
            <span class="silkscreen">Document</span>
            <!-- READING is a real state and it is not instant: decoding a 12 MP
               photo down to grid width takes long enough on an older phone
               that the control looked broken. `useAction` flips `busy` before
               the first await, so the label changes in the same frame as the
               tap, and a second tap while it is reading is dropped rather than
               starting a second decode. -->
            <button type="button" class="part" :disabled="load.busy.value" @click="pick">
              <span class="tray-title">{{ documentLabel }}</span>
              <div class="silkscreen">Never leaves this device</div>
            </button>
            <!-- A second tray. Always shown; the picker itself says so when no provider is configured:
               a button that opens a box that cannot work is worse than no
               button. -->
            <button
              type="button" class="part" :disabled="load.busy.value"
              @click="openGifs"
            >
              <span class="tray-title">{{ say('FIND A GIF', 'Find a GIF') }}</span>
              <div class="silkscreen">{{ say('IT WILL MOVE. GIFS BY KLIPY AND GIPHY', 'It will move. GIFs by KLIPY and GIPHY') }}</div>
            </button>
            <!-- The third tray: five seconds of a video. Native only in practice —
               Chrome and Firefox will not decode an iPhone's HEVC .mov — but the
               control is offered wherever a video can be chosen and the trimmer
               says so plainly if the platform refuses the file. -->
            <button type="button" class="part" :disabled="load.busy.value" @click="pickVideo">
              <span class="tray-title">{{ say('CHOOSE A VIDEO', 'Choose a video') }}</span>
              <div class="silkscreen">{{ say('FIVE SECONDS OF IT. IT WILL MOVE.', 'Five seconds of it. It will move.') }}</div>
            </button>
          </div>
          <div v-else class="sheet preview-box">
            <img :src="previewSrc" :alt="draft.kind === 'clip' ? 'The first frame of your clip' : 'The photo you chose'" />
            <button type="button" class="clear" :aria-label="say('CLEAR', 'Remove the picture')" @click="clearMedia">
              <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" /></svg>
            </button>
          </div>
          <input
            ref="fileEl" type="file" accept="image/*,image/gif" hidden
            @change="load.run($event)"
          >
          <input ref="videoEl" type="file" accept="video/*" hidden @change="onVideo">
          <GifPicker v-if="gifPicker" @pick="pickedGif" @close="gifPicker = false" />
          <VideoTrimmer v-if="trimFile" :file="trimFile" @done="trimmed" @close="trimFile = null" />
          <p v-if="load.error.value" class="field-error">{{ load.error.value }}</p>

          <!-- The error belongs beside the control that failed, not in a toast
               the person has to catch. HEIC is the common case and it is not
               their fault, so the line says what to do next. -->
        </div>

        <!-- THE COVER SHEET IS A SHEET. It was a settings-style form; the page
             the machine prints is TO / FROM / RE and a memo on paper, so the
             screen that writes it now looks like the page it writes. -->
        <div class="sheet cover-paper" role="group" :aria-label="say('COVER SHEET', 'Cover sheet')">
          <div class="cover-row">
            <IonTextarea
              class="cover-field caps" label="TO:" label-placement="start"
              :value="draft.toName" placeholder="MUM" autocapitalize="characters"
              maxlength="24" enterkeyhint="next" :auto-grow="true" :rows="1"
              @keydown.enter.prevent
              @ionInput="draft.toName = oneLine($event.target.value)"
            />
            <IonTextarea
              class="cover-field caps" label="FROM:" label-placement="start"
              :value="m.tsi" placeholder="ME" autocapitalize="characters"
              maxlength="20" enterkeyhint="next" :auto-grow="true" :rows="1"
              @keydown.enter.prevent
              @ionInput="renameMachine($event.target.value)"
            />
          </div>
          <IonTextarea
            class="cover-field caps" label="RE:" label-placement="start"
            :value="draft.subject" placeholder="THE DOG" autocapitalize="characters"
            maxlength="46" enterkeyhint="next" :auto-grow="true" :rows="1"
            @keydown.enter.prevent
            @ionInput="draft.subject = oneLine($event.target.value)"
          />
          <IonTextarea
            class="cover-field memo" :aria-label="say('MEMO', 'Memo')"
            :value="draft.memo" placeholder="She ate the report. Again."
            :maxlength="hasImage ? 58 : 480" :counter="true" :auto-grow="true" :rows="2"
            :helper-text="memoHelp"
            @ionInput="draft.memo = $event.target.value"
          />
        </div>

        <!-- CONDITION, here rather than on a preview screen: the page goes
             straight from this sheet into the machine. Three tappable
             positions, a real radiogroup, arrow keys between them. -->
        <div>
          <p class="silkscreen legend" aria-hidden="true">
            <span>Condition</span><span>clean ——— destroyed</span>
          </p>
          <div class="dial" role="radiogroup" aria-label="Condition" @keydown="onDialKey">
            <button
              v-for="(cond, i) in CONDITIONS"
              :key="cond"
              ref="dialEls"
              type="button"
              role="radio"
              :aria-checked="draft.condition === cond"
              :tabindex="tabIndexFor(cond, i)"
              @click="setCondition(cond)"
            >
              {{ cond }}
            </button>
          </div>
          <p class="silkscreen says" style="margin: var(--sp-2) 0 0" role="status">
            Condition: {{ draft.condition }}<template v-if="draft.condition === 'mint'"> · nothing is damaged at mint</template>
          </p>
        </div>

        <IonButton
          expand="block" size="large"
          :disabled="!canSend || send.busy.value" @click="send.run()"
        >
          {{ sendLabel }}
        </IonButton>
        <!-- A DISABLED BUTTON WITH NO EXPLANATION IS A DEAD END. The rule for
             what makes a page is one line long; say it, rather than greying
             the control out and leaving the person to guess which of the four
             fields above was the one that mattered. -->
        <p v-if="!canSend" class="silkscreen says center" style="margin: 0">
          {{ say(
            'LOAD A PHOTO, OR TYPE A NAME OR A MEMO.',
            'Choose a photo, or type a name or a memo, and the machine can print a page.',
          ) }}
        </p>
        <p v-if="send.error.value" class="field-error">{{ send.error.value }}</p>
      </div>
      <div class="tail" />
    </IonContent>
  </IonPage>
</template>

<script setup>
import { canPickClipNatively, pickClipNatively } from '../fax/clipnative.js';
import VideoTrimmer from '../components/VideoTrimmer.vue';
import { packClip, blobFromDataUrl } from '../fax/animate.js';
import { isNative, plugin } from '../core/native.js';
import GifPicker from '../components/GifPicker.vue';
import { computed, reactive, ref, onMounted } from 'vue';
import { IonPage, IonContent, IonButton, IonTextarea } from '@ionic/vue';
import { useRouter, useRoute } from 'vue-router';
import { AppHeader, useAction } from '../core/ui';
import { addFax, findFax, saveFax, storeReady } from '../store.js';
import { CONDITIONS } from '../fax/geometry.js';
import { machine, ensureMachine, updateMachine, tsi } from '../fax/machine.js';
import { GRID_W } from '../fax/geometry.js';
import { say } from '../fax/voice.js';
import { haptic, toast } from '../core/util.js';

const router = useRouter();
const route = useRoute();
const m = machine;
const fileEl = ref(null);

const draft = reactive({ toName: '', subject: '', memo: '', source: '', kind: 'photo', condition: 'used' });
// The row being edited, when Back from the transmission brought the page
// here. SEND then updates it instead of printing a second copy into the outbox.
let editing = null;

// --- CONDITION ----------------------------------------------------------------
const dialEls = ref([]);
function setCondition(cond) {
  if (draft.condition === cond) return;
  haptic('selection');
  draft.condition = cond;
  requestAnimationFrame(() => { dialEls.value?.[CONDITIONS.indexOf(cond)]?.focus(); });
}
/** Arrow keys move a radiogroup, as the role promises. */
function onDialKey(e) {
  const keys = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
  const step = keys[e.key];
  if (!step) return;
  e.preventDefault();
  const at = CONDITIONS.indexOf(draft.condition);
  setCondition(CONDITIONS[(at + step + CONDITIONS.length) % CONDITIONS.length]);
}
/** Roving tabindex: one tab stop, and it is the selected position. */
const tabIndexFor = (cond, i) => {
  const at = CONDITIONS.indexOf(draft.condition);
  return cond === draft.condition || (at === -1 && i === 0) ? 0 : -1;
};

// --- video --------------------------------------------------------------------
const videoEl = ref(null);
const trimFile = ref(null);
// Native first. On iOS this is Apple's trimmer with a five-second cap and the
// frames read natively; the sheet below only exists for Android and the web.
async function pickVideo() {
  haptic('tick');
  if (canPickClipNatively()) {
    try {
      const clip = await pickClipNatively();
      if (clip) trimmed(clip);
    } catch {
      toast(say('THIS MACHINE CANNOT READ THAT VIDEO.', "This machine can't read that video."));
    }
    return;
  }
  videoEl.value?.click();
}
function onVideo(e) {
  const f = e?.target?.files?.[0];
  if (e?.target) e.target.value = '';
  if (f) trimFile.value = f;
}
function trimmed({ frames, delaysMs }) {
  trimFile.value = null;
  draft.source = packClip({ frames, delaysMs });
  draft.kind = 'clip';
}

// --- GIFs -------------------------------------------------------------------
const gifPicker = ref(false);
const openGifs = () => { haptic('tick'); gifPicker.value = true; };

/** Keep the GIF's own bytes, as a data URL, exactly like a photo is kept. */
async function loadGif(fileOrBlob) {
  draft.source = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(fileOrBlob);
  });
  draft.kind = 'gif';
}
async function pickedGif(blob) {
  gifPicker.value = false;
  await loadGif(blob);
}
// THE TRAY IS ALWAYS THERE. It used to appear only after a probe of the GIF
// route answered, which meant the button popped in a beat after the page —
// and on a slow link, or offline, never. A machine's buttons do not come and
// go. If the server has no provider the picker says so itself (503 → "NO GIF
// PROVIDER IS CONFIGURED ON THIS MACHINE").
/** One line, whatever a paste brought in. */
const oneLine = (v) => String(v ?? '').replace(/[\r\n]+/g, ' ');
/** CLEAR: back to an empty tray, and the three ways to fill it. */
function clearMedia() {
  haptic('tick');
  draft.source = '';
  draft.kind = 'photo';
}
const hasImage = computed(() => !!draft.source);
// A clip's `source` is JSON of its frames; the sheet shows the first one.
const previewSrc = computed(() => {
  if (draft.kind !== 'clip') return draft.source;
  try { return JSON.parse(draft.source).frames[0] || ''; } catch { return ''; }
});
const canSend = computed(() => hasImage.value || !!draft.toName || !!draft.memo);

/**
 * ON THE PHONE, THE SYSTEM PICKER WITH ITS OWN CROP. Capacitor's Camera plugin
 * with allowEditing hands the photo to iOS's move-and-scale editor (Android:
 * its crop intent) before it comes back here, so the picture can be framed
 * before it is dithered. The result arrives as a data URL and goes through
 * the same reader as a picked file. Cancelling the picker is not an error.
 * The web keeps the file input — there is no native editor to hand it to.
 */
const pick = async () => {
  haptic('tick');
  const Camera = isNative ? plugin('Camera') : null;
  if (!Camera?.getPhoto) { fileEl.value?.click(); return; }
  let shot;
  try {
    shot = await Camera.getPhoto({
      source: 'PHOTOS', resultType: 'dataUrl', allowEditing: true,
      quality: 92, correctOrientation: true,
    });
  } catch (e) {
    if (/cancel/i.test(String(e?.message || e))) return;
    fileEl.value?.click();
    return;
  }
  if (!shot?.dataUrl) return;
  const blob = blobFromDataUrl(shot.dataUrl);
  const file = new File([blob], `photo.${shot.format || 'jpeg'}`, { type: blob.type });
  await load.run({ target: { files: [file], value: '' } });
};

/** One field, one fact: FROM on the cover sheet IS the machine's name. */
const renameMachine = (v) => updateMachine({ tsi: String(v || '').toUpperCase() });
const understood = () => { haptic('tick'); updateMachine({ toldOnce: true }); };

/**
 * Decode SMALL. Never a full-resolution photo: a 12 MP image is ~48 MB as
 * RGBA, and iOS Safari does not throw when canvas memory runs out — it returns
 * a BLANK canvas, so the failure is a silently empty fax rather than an error.
 */
const load = useAction(async (e) => {
  const input = e?.target;
  const file = input?.files?.[0];
  if (!file) return;
  // A GIF IS NOT A PHOTO. The path below draws the file onto a canvas and
  // re-encodes it as JPEG, which keeps one frame and throws the rest away. A
  // GIF is kept as its own bytes so the render can take it apart later.
  if (file.type === 'image/gif') {
    if (input) input.value = '';
    await loadGif(file);
    return;
  }
  let bmp;
  try {
    bmp = await createImageBitmap(file, { resizeWidth: GRID_W, resizeQuality: 'high' });
  } catch {
    // HEIC from a web file input cannot be decoded by Chrome or Firefox.
    draft.source = '';
    // Thrown, not toasted: `useAction` renders it beside the control that
    // failed and announces it, which a toast floating over the bottom of the
    // screen does neither of.
    throw new Error('THIS MACHINE CANNOT READ THAT FORMAT. TRY A JPEG OR A PNG.');
  } finally {
    // Otherwise picking the same file twice in a row fires no change event.
    if (input) input.value = '';
  }
  const c = document.createElement('canvas');
  c.width = bmp.width; c.height = bmp.height;
  c.getContext('2d').drawImage(bmp, 0, 0);
  bmp.close?.();
  draft.source = c.toDataURL('image/jpeg', 0.86);
  // A screenshot is a camera-roll photo, but it dithers better with an
  // ordered matrix — flat UI blocks become halftone instead of black slabs.
  draft.kind = /screenshot/i.test(file.name) ? 'screenshot' : 'photo';
  c.width = c.height = 0;
  // No toast on success. The photo appearing on the page IS the confirmation,
  // and a toast saying so on top of it is the noise that teaches people to
  // ignore the toast that matters.
}, { weight: 'tick', failed: 'THIS MACHINE CANNOT READ THAT FORMAT.' });

// `addFax` writes to IndexedDB and `ensureMachine` may still be resolving, so
// this is not instant — and two taps used to produce two faxes and two rows in
// the outbox. `useAction` drops the second.
const send = useAction(async () => {
  await storeReady;
  await ensureMachine();
  const fields = {
    kind: hasImage.value ? draft.kind : 'memo',
    toName: draft.toName.toUpperCase(),
    fromName: tsi.value,
    subject: draft.subject,
    memo: draft.memo,
    source: draft.source,
    condition: draft.condition,
    pages: 1,
    status: 'draft',
  };
  const row = editing
    ? saveFax({ ...editing, ...fields })
    : addFax({ ...fields, seed: Math.floor(Math.random() * 2 ** 31), createdAt: new Date().toISOString() });
  editing = row;
  router.push(`/tabs/machine/${row.id}/sending`);
}, { weight: 'pop', failed: 'THE MACHINE COULD NOT TAKE THAT PAGE.' });

// Both controls say what they are doing while they are doing it. A control
// that looks identical for 900ms is the single most common feedback defect.
const documentLabel = computed(() => {
  if (load.busy.value) return say('READING ORIGINAL…', 'Reading the photo…');
  return hasImage.value
    ? say('CHANGE PHOTO', 'Choose a different photo')
    : say('CHOOSE A PHOTO', 'Choose a photo');
});
const sendLabel = computed(() => {
  if (send.busy.value) return say('FEEDING…', 'Feeding the page…');
  if (hasImage.value) return say('SEND', 'Make this fax');
  return say('SEND COVER SHEET', 'Make this cover sheet');
});

/** The memo prints differently with a photo, so it says so before it is typed. */
const memoHelp = computed(() => (hasImage.value
  ? 'One line, printed across the top of the photo.'
  : 'Printed under the cover sheet, wrapped over as many lines as it needs.'));

onMounted(async () => {
  await storeReady;
  await ensureMachine();
  // Back from the transmission: ?fax=ID puts that page back on the sheet.
  const id = route.query.fax;
  const prior = id ? findFax(String(id)) : null;
  if (prior) {
    editing = prior;
    draft.toName = prior.toName || '';
    draft.subject = prior.subject || '';
    draft.memo = prior.memo || '';
    draft.source = prior.source || '';
    draft.kind = prior.kind === 'memo' ? 'photo' : (prior.kind || 'photo');
    draft.condition = CONDITIONS.includes(prior.condition) ? prior.condition : 'used';
  }
  // Arriving from a shared link: ?re=NAME pre-fills TO.
  // SANITISED ON READ — it is rendered onto an image a third party will see.
  const re = String(route.query.re || '')
    .toUpperCase().replace(/[^A-Z0-9 .&'-]/g, '').slice(0, 20);
  if (re) draft.toName = re;
});
</script>

<style scoped>
/* THE PREVIEW IS A FIXED BOX. A portrait clip at natural height filled the
   whole screen and pushed the cover sheet and SEND below the fold; landscape
   or portrait, the picture now sits letterboxed in the same paper-coloured
   area and the form stays where it was. */
.preview-box { position: relative; height: clamp(160px, 30vh, 300px); }
.clear {
  /* A key, not a glyph: the ✕ was a thin character floating in a pale disc
     that vanished on a light photo. Ink disc, paper cross, a hairline of paper
     round the edge so it reads on any picture. */
  position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; padding: 0;
  display: grid; place-items: center;
  border: 0; border-radius: 50%;
  background: var(--legend, #2b2723); color: var(--paper, #f7f5f0);
  box-shadow: 0 0 0 2px rgba(247, 245, 240, 0.9), 0 2px 6px rgba(0, 0, 0, 0.35);
  cursor: pointer; transition: transform 120ms ease;
}
.clear svg { width: 16px; height: 16px; stroke: currentColor; stroke-width: 2.6; stroke-linecap: round; fill: none; }
.clear:active { transform: scale(0.92); }
.clear:focus-visible { outline: 3px solid var(--led-red); outline-offset: 2px; }
.preview-box img { width: 100%; height: 100%; object-fit: contain; }
.cover-paper { padding: var(--sp-2) var(--sp-3) var(--sp-1); font-family: var(--mono); }
.cover-row { display: flex; flex-wrap: wrap; gap: 0 var(--sp-3); }
.cover-row > * { flex: 1 1 45%; min-width: 0; }
.cover-field {
  --background: transparent; --color: #000;
  --placeholder-color: #8a8a8a; --placeholder-opacity: 1;
  --border-color: #000; --border-width: 0 0 1px 0; --highlight-color-focused: var(--led-red);
  --padding-start: 0; --padding-end: 0;
  font-family: var(--mono); font-weight: 600; min-height: var(--tap);
}
.cover-field.caps { text-transform: uppercase; }
.cover-field.memo { font-weight: 400; margin-top: var(--sp-1); }
.legend { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 0 var(--sp-3); margin: 0 0 var(--sp-2); }
</style>
